"use client";

import React, { useState } from "react";
import { Check, ChevronRight, Search, PenTool, Compass, Layers, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: 1,
  title: "Discovery",
  icon: Search,
  description: "Understanding your unique financial DNA.",
  details: ["Deep dive into financials", "Goal setting & values", "Risk tolerance assessment", "Document collection"]
}, {
  id: 2,
  title: "Architecture",
  icon: PenTool,
  description: "Designing the structural blueprint.",
  details: ["Wealth structuring", "Comprehensive tax analysis", "Estate plan review", "Gap analysis"]
}, {
  id: 3,
  title: "Strategy",
  icon: Compass,
  description: "Tax-efficient planning & selection.",
  details: ["Investment policy creation", "Asset allocation strategy", "Tax mitigation tactics", "Insurance needs analysis"]
}, {
  id: 4,
  title: "Implementation",
  icon: Layers,
  description: "Executing the plan with precision.",
  details: ["Portfolio construction", "Account setup & transfer", "Strategy execution", "Coordination with CPAs"]
}, {
  id: 5,
  title: "Legacy",
  icon: Flag,
  description: "Ensuring your wealth endures.",
  details: ["Multi-generational planning", "Philanthropic strategy", "Trust funding verification", "Next-gen education"]
}];
export default function ThePlanningProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [activeStep, setActiveStep] = useState<number | null>(null);
  return <section id="the-planning-process" ref={ref} className={`dark bg-background text-foreground py-20 lg:py-32 overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 relative z-10">
        {}
        <div className="text-center max-w-3xl mx-auto mb-20 lg:mb-32">
          <h2 className="text-secondary font-medium tracking-widest uppercase text-sm mb-4">
            Our Methodology
          </h2>
          <h3 className="text-4xl md:text-5xl font-serif text-foreground mb-6">
            The Planning Process
          </h3>
          <p className="text-muted-foreground text-lg leading-relaxed">
            A disciplined, five-step approach designed to uncover opportunities,
            mitigate risks, and align your wealth with your life's purpose.
          </p>
        </div>

        {}
        <div className="hidden lg:block relative px-10">
          {}
          <div className="absolute top-1/2 left-0 w-full -translate-y-1/2 px-10 z-0">
            <Separator className="bg-secondary/30 h-[2px]" />
          </div>

          <div className="relative z-10 grid grid-cols-5 gap-4">
            {steps.map((step, i) => <div key={step.id} data-index={i} className="flex flex-col items-center group" onMouseEnter={() => setActiveStep(step.id)} onMouseLeave={() => setActiveStep(null)}>
                <HoverCard openDelay={0} closeDelay={100}>
                  <HoverCardTrigger asChild>
                    <div className="relative outline-none cursor-pointer" tabIndex={0} role="button" aria-label={`View details for ${step.title}`}>
                      {}
                      <div className={`w-16 h-16 rounded-full border-2 flex items-center justify-center transition-all duration-500 bg-background ${activeStep === step.id ? "border-secondary shadow-[0_0_20px_rgba(212,175,55,0.3)] scale-110" : "border-muted-foreground/30 hover:border-secondary/70"}`}>
                        <step.icon className={`w-6 h-6 transition-colors duration-300 ${activeStep === step.id ? "text-secondary" : "text-muted-foreground"}`} />
                      </div>
                      
                      {}
                      <div className="absolute top-20 left-1/2 -translate-x-1/2 w-48 text-center">
                        <h4 className={`font-serif text-xl transition-colors duration-300 ${activeStep === step.id ? "text-secondary" : "text-foreground"}`}>
                          {step.title}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </HoverCardTrigger>
                  
                  <HoverCardContent side="top" align="center" className="w-72 bg-card border-secondary/20 p-6 shadow-xl">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-secondary/10 rounded-full">
                          <step.icon className="w-5 h-5 text-secondary" />
                        </div>
                        <h4 className="font-serif text-lg text-foreground">
                          {step.title}
                        </h4>
                      </div>
                      <Separator className="bg-border" />
                      <ul className="space-y-2">
                        {step.details.map((detail, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                            <span>{detail}</span>
                          </li>)}
                      </ul>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </div>)}
          </div>
        </div>

        {}
        <div className="lg:hidden relative pl-8 border-l border-secondary/30 ml-4 space-y-12">
          {steps.map((step, i) => <div key={step.id} data-index={i} className="relative pl-8">
              {}
              <div className="absolute -left-[41px] top-0 flex items-center justify-center w-6 h-6 rounded-full bg-background border-2 border-secondary z-10">
                <div className="w-2 h-2 bg-secondary rounded-full" />
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <step.icon className="w-6 h-6 text-secondary" />
                  <h4 className="text-2xl font-serif text-foreground">
                    {step.title}
                  </h4>
                </div>
                <p className="text-muted-foreground">{step.description}</p>
                
                <div className="bg-card/50 border border-border p-5 rounded-lg">
                  <ul className="space-y-3">
                    {step.details.map((detail, idx) => <li key={idx} data-index={idx} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />
                        <span>{detail}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>)}
        </div>

        {}
        <div className="mt-24 text-center">
          <Link to="/WealthArchitecturePage">
            <Button size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 min-w-[200px]">
              Start Your Journey
              <ChevronRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}